import React, { useRef, useState } from 'react';
import GridBackground from '../components/background/GridBackground';
import VariableProximity from '../components/text/VariableProximity';
import Shuffle from '../components/text/Shuffle';
import TextPressure from '../components/text/TextPressure';
import TextCursor from '../components/text/TextCursor';
import ScrollReveal from '../components/text/ScrollReveal';
import ScrambledText from '../components/text/ScrambledText';
import Loader from '../components/loader/Loader';
import PageTransition from '../components/animations/PageTransition';
import Folder from '../components/animations/Folder';
import LiquidFolder from '../components/animations/LiquidFolder';
import PixelTransition from '../components/animations/PixelTransition';
import LaserFlow from '../components/animations/LaserFlow';
import MagnetLines from '../components/animations/MagnetLines';
import GhostCursor from '../components/animations/GhostCursor';
import Cubes from '../components/animations/Cubes';
import ImageTrail from '../components/animations/ImageTrail';
import ScrollStack, { ScrollStackItem } from '../components/animations/ScrollStack';
import BubbleMenu from '../components/navbar/BubbleMenu';
import CardNav from '../components/navbar/CardNav';
import CircularGallery from '../components/gallery/CircularGallery';
import Stack from '../components/gallery/Stack';
import GlassSurface from '../components/ui/GlassSurface';
import GlassSurfaceNavbar from '../components/ui/GlassSurfaceNavbar';
import DomeGallery from '../components/ui/DomeGallery';

const squaresCode = `import React, { useRef, useEffect } from "react";

const Squares = ({
  mousePosition,
  direction = 'right',
  speed = 1,
  borderColor = '#999',
  squareSize = 40,
  hoverFillColor = '#222',
  className = ''
}) => {
  const canvasRef = useRef(null);
  const requestRef = useRef(null);
  const gridOffset = useRef({ x: 0, y: 0 });
  const hoveredSquare = useRef(null);

  useEffect(() => {
    if (mousePosition) {
      const hoveredSquareX = Math.floor((mousePosition.x + (gridOffset.current.x % squareSize)) / squareSize);
      const hoveredSquareY = Math.floor((mousePosition.y + (gridOffset.current.y % squareSize)) / squareSize);
      hoveredSquare.current = { x: hoveredSquareX, y: hoveredSquareY };
    } else {
      hoveredSquare.current = null;
    }
  }, [mousePosition, squareSize]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();

    const drawGrid = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const startX = 0;
      const startY = 0;

      for (let x = startX; x < canvas.width + squareSize; x += squareSize) {
        for (let y = startY; y < canvas.height + squareSize; y += squareSize) {
          const squareX = x - (gridOffset.current.x % squareSize);
          const squareY = y - (gridOffset.current.y % squareSize);

          const currentSquareIndexX = Math.floor(x / squareSize);
          const currentSquareIndexY = Math.floor(y / squareSize);

          if (
            hoveredSquare.current &&
            currentSquareIndexX === hoveredSquare.current.x &&
            currentSquareIndexY === hoveredSquare.current.y
          ) {
            ctx.fillStyle = hoverFillColor;
            ctx.fillRect(squareX, squareY, squareSize, squareSize);
          }

          ctx.strokeStyle = borderColor;
          ctx.strokeRect(squareX, squareY, squareSize, squareSize);
        }
      }
    };

    const updateAnimation = () => {
      const effectiveSpeed = Math.max(speed, 0.1);
      switch (direction) {
        case 'right':
          gridOffset.current.x = (gridOffset.current.x - effectiveSpeed + squareSize) % squareSize;
          break;
        case 'left':
          gridOffset.current.x = (gridOffset.current.x + effectiveSpeed + squareSize) % squareSize;
          break;
        case 'up':
          gridOffset.current.y = (gridOffset.current.y + effectiveSpeed + squareSize) % squareSize;
          break;
        case 'down':
          gridOffset.current.y = (gridOffset.current.y - effectiveSpeed + squareSize) % squareSize;
          break;
        case 'diagonal':
          gridOffset.current.x = (gridOffset.current.x - effectiveSpeed + squareSize) % squareSize;
          gridOffset.current.y = (gridOffset.current.y - effectiveSpeed + squareSize) % squareSize;
          break;
        default:
          break;
      }

      drawGrid();
      requestRef.current = requestAnimationFrame(updateAnimation);
    };

    requestRef.current = requestAnimationFrame(updateAnimation);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [direction, speed, borderColor, hoverFillColor, squareSize]);

  return <canvas ref={canvasRef} className={\`w-full h-full border-none block \${className}\`}></canvas>;
};

export default Squares;`;

const gridBackgroundCode = `import React, { useState, useRef } from "react";
import Squares from "./Squares";

const GridBackground = ({ children, className = '' }) => {
  const [mousePosition, setMousePosition] = useState(null);
  const parentRef = useRef(null);

  const handleMouseMove = (event) => {
    const parent = parentRef.current;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    setMousePosition({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  };

  const handleMouseLeave = () => {
    setMousePosition(null);
  };

  return (
    <div
      ref={parentRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={\`relative w-full h-full overflow-hidden \${className}\`}
    >
      <div className="absolute inset-0 w-full h-full pointer-events-none">
        <Squares
          mousePosition={mousePosition}
          speed={0.5}
          squareSize={40}
          direction='diagonal'
          borderColor='rgba(128,128,128,0.1)'
          hoverFillColor='rgba(128,128,128,0.3)'
        />
      </div>
      <div className="relative z-10 h-full">{children}</div>
    </div>
  );
};

export default GridBackground;`;

const variableProximityCode = `import React, { useRef, useEffect, useMemo } from "react";

// --- Helper Hook: useAnimationFrame ---
function useAnimationFrame(callback) {
  useEffect(() => {
    let frameId;
    const loop = () => {
      callback();
      frameId = requestAnimationFrame(loop);
    };
    frameId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frameId);
  }, [callback]);
}

// --- Helper Hook: useMousePositionRef ---
function useMousePositionRef(containerRef) {
  const positionRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const updatePosition = (x, y) => {
      if (containerRef?.current) {
        const rect = containerRef.current.getBoundingClientRect();
        positionRef.current = { x: x - rect.left, y: y - rect.top };
      } else {
        positionRef.current = { x, y };
      }
    };
    const handleMouseMove = (ev) => updatePosition(ev.clientX, ev.clientY);
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [containerRef]);

  return positionRef;
}

// --- Reusable, Self-Contained Effect Component ---
const VariableProximity = ({ label, className, containerRef, fromSettings, toSettings, radius }) => {
  const letterRefs = useRef([]);
  const mousePositionRef = useMousePositionRef(containerRef);

  const parsedSettings = useMemo(() => {
    const parse = (str) => new Map(str.split(",").map(s => {
      const [axis, value] = s.trim().split(" ");
      return [axis.replace(/['"]/g, ""), parseFloat(value)];
    }));
    const from = parse(fromSettings);
    const to = parse(toSettings);
    return Array.from(from.entries()).map(([axis, fromValue]) => ({
      axis,
      fromValue,
      toValue: to.get(axis) ?? fromValue,
    }));
  }, [fromSettings, toSettings]);

  useAnimationFrame(() => {
    if (!containerRef?.current) return;
    const { x, y } = mousePositionRef.current;
    const containerRect = containerRef.current.getBoundingClientRect();

    letterRefs.current.forEach((letterRef) => {
      if (!letterRef) return;
      const rect = letterRef.getBoundingClientRect();
      const letterCenterX = rect.left + rect.width / 2 - containerRect.left;
      const letterCenterY = rect.top + rect.height / 2 - containerRect.top;
      const distance = Math.sqrt((x - letterCenterX) ** 2 + (y - letterCenterY) ** 2);
      const falloff = Math.max(0, 1 - distance / radius);

      const newSettings = parsedSettings.map(({ axis, fromValue, toValue }) => {
        const value = fromValue + (toValue - fromValue) * falloff;
        return \`'\${axis}' \${value}\`;
      }).join(", ");
      
      letterRef.style.fontVariationSettings = newSettings;
    });
  });

  return (
    <div className={className} style={{ fontFamily: '"Roboto Flex", sans-serif' }}>
      {label.split(" ").map((word, wordIndex, words) => (
        <span key={wordIndex} className="inline-block whitespace-nowrap">
          {word.split("").map((letter, letterIndex) => (
            <span key={letterIndex} ref={(el) => letterRefs.current.push(el)} className="inline-block">
              {letter}
            </span>
          ))}
          {wordIndex < words.length - 1 && '\\u00A0' /* Non-breaking space */}
        </span>
      ))}
    </div>
  );
};

export default VariableProximity;`;

const loaderCode = `import React from "react";

const Loader = () => {
  const spinnerStyle = {
    boxSizing: 'border-box',
    display: 'block',
    position: 'fixed',
    top: '50%',
    left: '50%',
    marginTop: '-5em',
    marginLeft: '-5em',
    width: '10em',
    height: '10em',
    transformStyle: 'preserve-3d',
    transformOrigin: '50%',
    transform: 'rotateY(50%)',
    perspectiveOrigin: '50% 50%',
    perspective: '340px',
    backgroundSize: '10em 10em',
    backgroundImage: \`url("data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9Im5vIj8+Cjxzdmcgd2lkdGg9IjI2NnB4IiBoZWlnaHQ9IjI5N3B4IiB2aWV3Qm94PSIwIDAgMjY2IDI5NyIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB4bWxuczpza2V0Y2g9Imh0dHA6Ly93d3cuYm9oZW1pYW5jb2RpbmcuY29tL3NrZXRjaC9ucyI+CiAgICA8dGl0bGU+c3Bpbm5lcjwvdGl0bGU+CiAgICA8ZGVzY3JpcHRpb24+Q3JlYXRlZCB3aXRoIFNrZXRjaCAoaHR0cDovL3d3dy5ib2hlbWlhbmNvZGluZy5jb20vc2tldGNoKTwvZGVzY3JpcHRpb24+CiAgICA8ZGVmcz48L2RlZnM+CiAgICA8ZyBpZD0iUGFnZS0xIiBzdHJva2U9Im5vbmUiIHN0cm9rZS11aWR0aD0iMSIgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIiBza2V0Y2g6dHlwZT0iTVNQYWdlIj4KICAgICAgICA8cGF0aCBkPSJNMTcxLjUwNzgxMywzLjI1MDAwMDM4IEMyMjYuMjA4MTgzLDEyLjg1NzcxMTEgMjk3LjExMjcyMiw3MS40OTEyODIzIDI1MC44OTU1OTksMTA4LjQxMDE1NSBDMjE2LjU4MjAyNCwxMzUuODIwMzEgMTg2LjUyODQwNSw5Ny4wNjI0OTY0IDE1Ni44MDA3NzQsODUuNzczNDM0NiBDMTI3LjA3MzE0Myw3NC40ODQzNzIxIDc2Ljg4ODQ2MzIsODQuMjE2MTQ2MiA2MC4xMjg5MDY1LDEwOC40MTAxNTMgQy0xNS45OTgwNDY4NSwyMTguMjgxMjQ3IDE0NS4yNzczNDQsMjk2LjY2Nzk2OCAxNDUuMjc3MzQ0LDI5Ni42Njc5NjggQzE0NS4yNzczNDQsMjk2LjY2Nzk2OCAtMjUuNDQ5MjE4NywyNTcuMjQyMTk4IDMuMzk4NDM3NSwxMDguNDEwMTU1IEMxNi4zMDcwNjYxLDQxLjgxMTQxNzQgODQuNzI3NTgyOSwtMTEuOTkyMjk4NSAxNzEuNTA3ODEzLDMuMjUwMDAwMzggWiIgaWQ9IlBhdGgtMSIgZmlsbD0iIzAwMDAwMCIgc2tldGNoOnR5cGU9Ik1TU2hhcGVHcm91cCI+PC9wYXRoPgogICAgPC9nPgo8L3N2Zz4=")\`,
  };

  return (
    <>
      <style>{\`
        @keyframes rotateBefore {
          from { transform: rotateX(60deg) rotateY(45deg) rotateZ(0deg); }
          to { transform: rotateX(60deg) rotateY(45deg) rotateZ(-360deg); }
        }
        @keyframes rotateAfter {
          from { transform: rotateX(240deg) rotateY(45deg) rotateZ(0deg); }
          to { transform: rotateX(240deg) rotateY(45deg) rotateZ(360deg); }
        }
        .spinner:before {
          transform: rotateX(60deg) rotateY(45deg) rotateZ(45deg);
          animation: 750ms rotateBefore infinite linear reverse;
        }
        .spinner:after {
          transform: rotateX(240deg) rotateY(45deg) rotateZ(45deg);
          animation: 750ms rotateAfter infinite linear;
        }
      \`}</style>
      <div className="relative w-full h-64 flex items-center justify-center bg-black overflow-hidden">
        <div className="spinner">
          <div className="spinner-before" style={{ ...spinnerStyle, animation: '750ms rotateBefore infinite linear reverse', transform: 'rotateX(60deg) rotateY(45deg) rotateZ(45deg)' }}></div>
          <div className="spinner-after" style={{ ...spinnerStyle, animation: '750ms rotateAfter infinite linear', transform: 'rotateX(240deg) rotateY(45deg) rotateZ(45deg)' }}></div>
        </div>
      </div>
    </>
  );
};

export default Loader;`;

const pageTransitionCode = `import React from "react";
import { motion } from "framer-motion";

const PageTransition = ({ children, location }) => {
  const variants = {
    initial: {
      opacity: 0,
      y: "100vh",
    },
    in: {
      opacity: 1,
      y: 0,
    },
    out: {
      opacity: 0,
      y: "-100vh",
    },
  };

  return (
    <motion.div
      key={location ? location.key : 'transition'}
      initial="initial"
      animate="in"
      exit="out"
      variants={variants}
      transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
      style={{ width: '100%', height: '100%' }}
    >
      {children}
    </motion.div>
  );
};

export default PageTransition;`;

const folderCode = `import { useState } from 'react';

const darkenColor = (hex, percent) => {
  let color = hex.startsWith('#') ? hex.slice(1) : hex;
  if (color.length === 3) {
    color = color
      .split('')
      .map(c => c + c)
      .join('');
  }
  const num = parseInt(color, 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  r = Math.max(0, Math.min(255, Math.floor(r * (1 - percent))));
  g = Math.max(0, Math.min(255, Math.floor(g * (1 - percent))));
  b = Math.max(0, Math.min(255, Math.floor(b * (1 - percent))));
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
};

const Folder = ({ color = '#5227FF', size = 1, items = [], className = '' }) => {
  const maxItems = 3;
  const papers = items.slice(0, maxItems);
  while (papers.length < maxItems) {
    papers.push(null);
  }

  const [open, setOpen] = useState(false);
  const [paperOffsets, setPaperOffsets] = useState(Array.from({ length: maxItems }, () => ({ x: 0, y: 0 })));

  const folderBackColor = darkenColor(color, 0.08);
  const paper1 = darkenColor('#ffffff', 0.1);
  const paper2 = darkenColor('#ffffff', 0.05);
  const paper3 = '#ffffff';

  const handleClick = () => {
    setOpen(prev => !prev);
    if (open) {
      setPaperOffsets(Array.from({ length: maxItems }, () => ({ x: 0, y: 0 })));
    }
  };

  const handlePaperMouseMove = (e, index) => {
    if (!open) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const offsetX = (e.clientX - centerX) * 0.15;
    const offsetY = (e.clientY - centerY) * 0.15;
    setPaperOffsets(prev => {
      const newOffsets = [...prev];
      newOffsets[index] = { x: offsetX, y: offsetY };
      return newOffsets;
    });
  };

  const handlePaperMouseLeave = (e, index) => {
    setPaperOffsets(prev => {
      const newOffsets = [...prev];
      newOffsets[index] = { x: 0, y: 0 };
      return newOffsets;
    });
  };

  const folderStyle = {
    '--folder-color': color,
    '--folder-back-color': folderBackColor,
    '--paper-1': paper1,
    '--paper-2': paper2,
    '--paper-3': paper3
  };

  const scaleStyle = { transform: \`scale(\${size})\` };

  const getOpenTransform = index => {
    if (index === 0) return 'translate(-120%, -70%) rotate(-15deg)';
    if (index === 1) return 'translate(10%, -70%) rotate(15deg)';
    if (index === 2) return 'translate(-50%, -100%) rotate(5deg)';
    return '';
  };

  return (
    <div style={scaleStyle} className={className}>
      <div
        className={\`group relative transition-all duration-200 ease-in cursor-pointer \${
          !open ? 'hover:-translate-y-2' : ''
        }\`}
        style={{
          ...folderStyle,
          transform: open ? 'translateY(-8px)' : undefined
        }}
        onClick={handleClick}
      >
        <div
          className="relative w-[100px] h-[80px] rounded-tl-0 rounded-tr-[10px] rounded-br-[10px] rounded-bl-[10px]"
          style={{ backgroundColor: folderBackColor }}
        >
          <span
            className="absolute z-0 bottom-[98%] left-0 w-[30px] h-[10px] rounded-tl-[5px] rounded-tr-[5px] rounded-bl-0 rounded-br-0"
            style={{ backgroundColor: folderBackColor }}
          ></span>
          {papers.map((item, i) => {
            let sizeClasses = '';
            if (i === 0) sizeClasses = open ? 'w-[70%] h-[80%]' : 'w-[70%] h-[80%]';
            if (i === 1) sizeClasses = open ? 'w-[80%] h-[80%]' : 'w-[80%] h-[70%]';
            if (i === 2) sizeClasses = open ? 'w-[90%] h-[80%]' : 'w-[90%] h-[60%]';

            const transformStyle = open
              ? \`\${getOpenTransform(i)} translate(\${paperOffsets[i].x}px, \${paperOffsets[i].y}px)\`
              : undefined;

            return (
              <div
                key={i}
                onMouseMove={e => handlePaperMouseMove(e, i)}
                onMouseLeave={e => handlePaperMouseLeave(e, i)}
                className={\`absolute z-20 bottom-[10%] left-1/2 transition-all duration-300 ease-in-out \${
                  !open ? 'transform -translate-x-1/2 translate-y-[10%] group-hover:translate-y-0' : 'hover:scale-110'
                } \${sizeClasses}\`}
                style={{
                  ...(!open ? {} : { transform: transformStyle }),
                  backgroundColor: i === 0 ? paper1 : i === 1 ? paper2 : paper3,
                  borderRadius: '10px'
                }}
              >
                {item}
              </div>
            );
          })}
          <div
            className={\`absolute z-30 w-full h-full origin-bottom transition-all duration-300 ease-in-out \${
              !open ? 'group-hover:[transform:skew(15deg)_scaleY(0.6)]' : ''
            }\`}
            style={{
              backgroundColor: color,
              borderRadius: '5px 10px 10px 10px',
              ...(open && { transform: 'skew(15deg) scaleY(0.6)' })
            }}
          ></div>
          <div
            className={\`absolute z-30 w-full h-full origin-bottom transition-all duration-300 ease-in-out \${
              !open ? 'group-hover:[transform:skew(-15deg)_scaleY(0.6)]' : ''
            }\`}
            style={{
              backgroundColor: color,
              borderRadius: '5px 10px 10px 10px',
              ...(open && { transform: 'skew(-15deg) scaleY(0.6)' })
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default Folder;`;

// Wrapper for VariableProximity demo
const VariableProximityDemo = () => {
  const containerRef = useRef(null);
  return (
    <div
      ref={containerRef}
      className="w-full h-full flex items-center justify-center bg-black cursor-default"
      style={{ minHeight: '300px' }}
    >
      <VariableProximity
        label="Hover Me to See the Effect"
        className="text-6xl md:text-8xl font-bold text-white tracking-tighter"
        fromSettings="'wght' 100, 'wdth' 85"
        toSettings="'wght' 900, 'wdth' 100"
        radius={100}
        containerRef={containerRef}
      />
    </div>
  );
};

// Wrapper for GridBackground demo
const GridBackgroundDemo = () => {
  return (
    <div className="w-full h-[400px] relative">
      <GridBackground>
        <div className="flex items-center justify-center h-full pointer-events-none">
          <h2 className="text-6xl font-bold text-white tracking-tighter px-6 py-4 rounded-xl underline">
            GRID BACKGROUD
          </h2>
        </div>
      </GridBackground>
    </div>
  );
};

// Wrapper for PageTransition demo
const PageTransitionDemo = () => {
  const [view, setView] = useState('view1');

  return (
    <div className="w-full h-[400px] relative bg-black border border-white/10 rounded-xl overflow-hidden flex flex-col">
      <div className="p-4 border-b border-white/10 flex justify-center gap-4">
        <button
          onClick={() => setView('view1')}
          className={`px-4 py-2 rounded ${view === 'view1' ? 'bg-white text-black' : 'text-white hover:bg-white/10'}`}
        >
          View 1
        </button>
        <button
          onClick={() => setView('view2')}
          className={`px-4 py-2 rounded ${view === 'view2' ? 'bg-white text-black' : 'text-white hover:bg-white/10'}`}
        >
          View 2
        </button>
      </div>
      <div className="relative flex-1 overflow-hidden">
        {view === 'view1' ? (
          <PageTransition location={{ key: 'view1' }}>
            <div className="w-full h-full flex items-center justify-center bg-blue-900/20 text-white text-2xl font-bold">
              Page 1 Content
            </div>
          </PageTransition>
        ) : (
          <PageTransition location={{ key: 'view2' }}>
            <div className="w-full h-full flex items-center justify-center bg-purple-900/20 text-white text-2xl font-bold">
              Page 2 Content
            </div>
          </PageTransition>
        )}
      </div>
    </div>
  );
};

// Wrapper for Folder demo
const FolderDemo = () => {
  return (
    <div className="w-full h-[400px] relative bg-black border border-white/10 rounded-xl overflow-hidden flex items-center justify-center">
      <Folder size={2} color="#5227FF" className="custom-folder" />
    </div>
  );
};

const liquidFolderCode = `import React from 'react';

const LiquidFolder = ({ items = [], color = '#5227FF', className = '' }) => {
    return (
        <div className={\`relative w-64 h-48 mx-auto mt-10 group cursor-pointer \${className}\`}>
            {/* Back Plate */}
            <div
                className="absolute inset-0 rounded-xl transition-transform duration-500 ease-out group-hover:scale-105"
                style={{
                    backgroundColor: color,
                    opacity: 0.5,
                    transform: 'perspective(1000px) rotateX(5deg)',
                }}
            ></div>

            {/* Items Container */}
            <div className="absolute inset-x-4 bottom-4 h-3/4 flex items-end justify-center space-x-[-20px] z-10">
                {items.map((item, index) => (
                    <div
                        key={index}
                        className="relative transition-all duration-500 ease-out group-hover:-translate-y-16 group-hover:rotate-0"
                        style={{
                            transform: \`rotate(\${index % 2 === 0 ? -5 : 5}deg) translateY(0)\`,
                            zIndex: index,
                            transitionDelay: \`\${index * 50}ms\`,
                        }}
                    >
                        <div className="w-24 h-32 bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 flex items-center justify-center">
                            {item}
                        </div>
                    </div>
                ))}
            </div>

            {/* Front Plate (Glass) */}
            <div
                className="absolute inset-0 z-20 rounded-xl border border-white/30 shadow-xl backdrop-blur-md transition-all duration-500 ease-out group-hover:backdrop-blur-sm"
                style={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.1) 100%)',
                    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
                }}
            >
                <div
                    className="absolute -top-4 left-0 w-1/3 h-6 rounded-t-lg border-t border-l border-r border-white/30 backdrop-blur-md"
                    style={{
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.1) 100%)',
                    }}
                ></div>
                <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-white/40 to-transparent opacity-50 pointer-events-none"></div>
            </div>
        </div>
    );
};

export default LiquidFolder;`;

// Wrapper for LiquidFolder demo
const LiquidFolderDemo = () => {
  const demoItems = [
    <span key="1" className="text-4xl">ðŸ“„</span>,
    <span key="2" className="text-4xl">ðŸ“ˆ</span>,
    <span key="3" className="text-4xl">ðŸ“¸</span>
  ];
  return (
    <div className="w-full h-[400px] relative bg-black border border-white/10 rounded-xl overflow-hidden flex items-center justify-center">
      <LiquidFolder items={demoItems} color="#2772FF" />
    </div>
  );
};

// Demo wrappers for new text components
const ShuffleDemo = () => {
  return (
    <div className="w-full h-[400px] relative bg-black border border-white/10 rounded-xl overflow-hidden flex items-center justify-center">
      <Shuffle
        text="Hello World"
        shuffleDirection="right"
        duration={0.35}
        animationMode="evenodd"
        shuffleTimes={1}
        ease="power3.out"
        stagger={0.03}
        threshold={0.1}
        triggerOnce={true}
        triggerOnHover={true}
        respectReducedMotion={true}
        className="text-white"
      />
    </div>
  );
};

const TextPressureDemo = () => {
  return (
    <div className="w-full h-[400px] relative bg-black border border-white/10 rounded-xl overflow-hidden">
      <TextPressure
        text="Hello!"
        flex={true}
        alpha={false}
        stroke={false}
        width={true}
        weight={true}
        italic={true}
        textColor="#ffffff"
        strokeColor="#ff0000"
        minFontSize={36}
      />
    </div>
  );
};

const TextCursorDemo = () => {
  return (
    <div className="w-full h-[400px] relative bg-black border border-white/10 rounded-xl overflow-hidden">
      <TextCursor
        text="Hello!"
        spacing={80}
        followMouseDirection={true}
        randomFloat={true}
        exitDuration={0.3}
        removalInterval={20}
        maxPoints={10}
      />
    </div>
  );
};



const ScrollRevealDemo = () => {
  return (
    <div className="w-full h-[600px] relative bg-black border border-white/10 rounded-xl overflow-y-auto">
      <div className="p-8 space-y-8">
        <div className="h-[200px] flex items-center justify-center text-white text-2xl">
          Scroll down to see the reveal effect
        </div>
        <ScrollReveal
          baseOpacity={0}
          enableBlur={true}
          baseRotation={5}
          blurStrength={10}
          textClassName="text-white"
        >
          When does a man die? When he is hit by a bullet? No! When he suffers a disease?
          No! When he ate a soup made out of a poisonous mushroom?
          No! A man dies when he is forgotten!
        </ScrollReveal>
        <div className="h-[200px]"></div>
      </div>
    </div>
  );
};

const shuffleCode = `import Shuffle from './Shuffle';

<Shuffle
  text="Hello World"
  shuffleDirection="right"
  duration={0.35}
  animationMode="evenodd"
  shuffleTimes={1}
  ease="power3.out"
  stagger={0.03}
  threshold={0.1}
  triggerOnce={true}
  triggerOnHover={true}
  respectReducedMotion={true}
/>`;

const textPressureCode = `import TextPressure from './TextPressure';

<div style={{position: 'relative', height: '300px'}}>
  <TextPressure
    text="Hello!"
    flex={true}
    alpha={false}
    stroke={false}
    width={true}
    weight={true}
    italic={true}
    textColor="#ffffff"
    strokeColor="#ff0000"
    minFontSize={36}
  />
</div>`;

const textCursorCode = `import TextCursor from './TextCursor';

<TextCursor
  text="Hello!"
  spacing={80}
  followMouseDirection={true}
  randomFloat={true}
  exitDuration={0.3}
  removalInterval={20}
  maxPoints={10}
/>`;



const scrollRevealCode = `import ScrollReveal from './ScrollReveal';

<ScrollReveal
  baseOpacity={0}
  enableBlur={true}
  baseRotation={5}
  blurStrength={10}
>
  When does a man die? When he is hit by a bullet? No! When he suffers a disease?
  No! When he ate a soup made out of a poisonous mushroom?
  No! A man dies when he is forgotten!
</ScrollReveal>`;

// Demo components for new animations
const ScrambledTextDemo = () => {
  return (
    <div className="w-full h-[400px] relative bg-black border border-white/10 rounded-xl overflow-hidden flex items-center justify-center">
      <ScrambledText
        radius={100}
        duration={1.2}
        speed={0.5}
        scrambleChars=".:"
        className="text-white"
      >
        Lorem ipsum dolor sit amet consectetur adipisicing elit.
      </ScrambledText>
    </div>
  );
};

const PixelTransitionDemo = () => {
  return (
    <div className="w-full h-[400px] relative bg-black border border-white/10 rounded-xl overflow-hidden flex items-center justify-center">
      <PixelTransition
        firstContent={
          <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <p className="text-white text-2xl font-bold">Hover Me!</p>
          </div>
        }
        secondContent={
          <div className="w-full h-full bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center">
            <p className="text-white text-3xl font-bold">âœ¨ Revealed!</p>
          </div>
        }
        gridSize={12}
        pixelColor="#ffffff"
        once={false}
        animationStepDuration={0.4}
      />
    </div>
  );
};

const LaserFlowDemo = () => {
  return (
    <div className="w-full h-[500px] relative bg-black border border-white/10 rounded-xl overflow-hidden">
      <LaserFlow
        horizontalBeamOffset={0.1}
        verticalBeamOffset={0.0}
        color="#FF79C6"
      />
    </div>
  );
};

const MagnetLinesDemo = () => {
  return (
    <div className="w-full h-[500px] relative bg-black border border-white/10 rounded-xl overflow-hidden flex items-center justify-center">
      <MagnetLines
        rows={9}
        columns={9}
        containerSize="60vmin"
        lineColor="tomato"
        lineWidth="0.8vmin"
        lineHeight="5vmin"
        baseAngle={0}
      />
    </div>
  );
};

const GhostCursorDemo = () => {
  return (
    <div className="w-full h-[600px] relative bg-black border border-white/10 rounded-xl overflow-hidden">
      <GhostCursor
        color="#B19EEF"
        brightness={1}
        edgeIntensity={0}
        trailLength={50}
        inertia={0.5}
        grainIntensity={0.05}
        bloomStrength={0.1}
        bloomRadius={1.0}
        bloomThreshold={0.025}
        fadeDelayMs={1000}
        fadeDurationMs={1500}
      />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <p className="text-white text-2xl">Move your mouse around</p>
      </div>
    </div>
  );
};

const scrambledTextCode = `import ScrambledText from './ScrambledText';

<ScrambledText
  radius={100}
  duration={1.2}
  speed={0.5}
  scrambleChars=".:"
>
  Lorem ipsum dolor sit amet consectetur adipisicing elit.
</ScrambledText>`;

const pixelTransitionCode = `import PixelTransition from './PixelTransition';

<PixelTransition
  firstContent={<div>First Content</div>}
  secondContent={<div>Second Content</div>}
  gridSize={12}
  pixelColor="#ffffff"
  once={false}
  animationStepDuration={0.4}
/>`;

const laserFlowCode = `import LaserFlow from './LaserFlow';

<div style={{ height: '500px', position: 'relative' }}>
  <LaserFlow
    horizontalBeamOffset={0.1}
    verticalBeamOffset={0.0}
    color="#FF79C6"
  />
</div>`;

const magnetLinesCode = `import MagnetLines from './MagnetLines';

<MagnetLines
  rows={9}
  columns={9}
  containerSize="60vmin"
  lineColor="tomato"
  lineWidth="0.8vmin"
  lineHeight="5vmin"
  baseAngle={0}
/>`;

const ghostCursorCode = `import GhostCursor from './GhostCursor';

<div style={{ height: 600, position: 'relative' }}>
  <GhostCursor
    color="#B19EEF"
    brightness={1}
    edgeIntensity={0}
    trailLength={50}
    inertia={0.5}
    grainIntensity={0.05}
    bloomStrength={0.1}
    bloomRadius={1.0}
    bloomThreshold={0.025}
    fadeDelayMs={1000}
    fadeDurationMs={1500}
  />
</div>`;

// Demo components for additional animations
const CubesDemo = () => {
  return (
    <div className="w-full h-[600px] relative bg-black border border-white/10 rounded-xl overflow-hidden flex items-center justify-center">
      <Cubes
        gridSize={8}
        maxAngle={60}
        radius={4}
        borderStyle="2px dashed #5227FF"
        faceColor="#1a1a2e"
        rippleColor="#ff6b6b"
        rippleSpeed={1.5}
        autoAnimate={true}
        rippleOnClick={true}
      />
    </div>
  );
};

const ImageTrailDemo = () => {
  const images = [
    'https://picsum.photos/id/287/300/300',
    'https://picsum.photos/id/1001/300/300',
    'https://picsum.photos/id/1025/300/300',
    'https://picsum.photos/id/1026/300/300',
    'https://picsum.photos/id/1027/300/300',
    'https://picsum.photos/id/1028/300/300',
    'https://picsum.photos/id/1029/300/300',
    'https://picsum.photos/id/1030/300/300'
  ];

  return (
    <div className="w-full h-[500px] relative bg-black border border-white/10 rounded-xl overflow-hidden">
      <ImageTrail items={images} variant={1} />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <p className="text-white text-2xl">Move your mouse around</p>
      </div>
    </div>
  );
};



const ScrollStackDemo = () => {
  return (
    <div className="w-full h-[600px] relative bg-black border border-white/10 rounded-xl overflow-hidden">
      <ScrollStack>
        <ScrollStackItem>
          <h2 className="text-white text-2xl font-bold mb-4">Card 1</h2>
          <p className="text-gray-300">This is the first card in the stack</p>
        </ScrollStackItem>
        <ScrollStackItem>
          <h2 className="text-white text-2xl font-bold mb-4">Card 2</h2>
          <p className="text-gray-300">This is the second card in the stack</p>
        </ScrollStackItem>
        <ScrollStackItem>
          <h2 className="text-white text-2xl font-bold mb-4">Card 3</h2>
          <p className="text-gray-300">This is the third card in the stack</p>
        </ScrollStackItem>
      </ScrollStack>
    </div>
  );
};

const cubesCode = `import Cubes from './Cubes';

<div style={{ height: '600px', position: 'relative' }}>
  <Cubes 
    gridSize={8}
    maxAngle={60}
    radius={4}
    borderStyle="2px dashed #5227FF"
    faceColor="#1a1a2e"
    rippleColor="#ff6b6b"
    rippleSpeed={1.5}
    autoAnimate={true}
    rippleOnClick={true}
  />
</div>`;

const imageTrailCode = `import ImageTrail from './ImageTrail';

<div style={{ height: '500px', position: 'relative', overflow: 'hidden'}}>
  <ImageTrail
    items={[
      'https://picsum.photos/id/287/300/300',
      'https://picsum.photos/id/1001/300/300',
      // ... more images
    ]}
    variant={1}
  />
</div>`;



const scrollStackCode = `import ScrollStack, { ScrollStackItem } from './ScrollStack';

<ScrollStack>
  <ScrollStackItem>
    <h2>Card 1</h2>
    <p>This is the first card in the stack</p>
  </ScrollStackItem>
  <ScrollStackItem>
    <h2>Card 2</h2>
    <p>This is the second card in the stack</p>
  </ScrollStackItem>
  <ScrollStackItem>
    <h2>Card 3</h2>
    <p>This is the third card in the stack</p>
  </ScrollStackItem>
</ScrollStack>`;

// Navbar components
const BubbleMenuDemo = () => {
  const items = [
    {
      label: 'home',
      href: '#',
      ariaLabel: 'Home',
      rotation: -8,
      hoverStyles: { bgColor: '#3b82f6', textColor: '#ffffff' }
    },
    {
      label: 'about',
      href: '#',
      ariaLabel: 'About',
      rotation: 8,
      hoverStyles: { bgColor: '#10b981', textColor: '#ffffff' }
    },
    {
      label: 'projects',
      href: '#',
      ariaLabel: 'Projects',
      rotation: 8,
      hoverStyles: { bgColor: '#f59e0b', textColor: '#ffffff' }
    },
    {
      label: 'blog',
      href: '#',
      ariaLabel: 'Blog',
      rotation: 8,
      hoverStyles: { bgColor: '#ef4444', textColor: '#ffffff' }
    },
    {
      label: 'contact',
      href: '#',
      ariaLabel: 'Contact',
      rotation: -8,
      hoverStyles: { bgColor: '#8b5cf6', textColor: '#ffffff' }
    }
  ];

  return (
    <div className="w-full h-[400px] relative bg-gradient-to-br from-purple-900 to-indigo-900 rounded-xl overflow-hidden">
      <BubbleMenu
        logo={<span style={{ fontWeight: 700 }}>RB</span>}
        items={items}
        menuAriaLabel="Toggle navigation"
        menuBg="#ffffff"
        menuContentColor="#111111"
        useFixedPosition={false}
        animationEase="back.out(1.5)"
        animationDuration={0.5}
        staggerDelay={0.12}
      />
    </div>
  );
};

const CardNavDemo = () => {
  const items = [
    {
      label: "About",
      bgColor: "#0D0716",
      textColor: "#fff",
      links: [
        { label: "Company", ariaLabel: "About Company", href: "#" },
        { label: "Careers", ariaLabel: "About Careers", href: "#" }
      ]
    },
    {
      label: "Projects",
      bgColor: "#170D27",
      textColor: "#fff",
      links: [
        { label: "Featured", ariaLabel: "Featured Projects", href: "#" },
        { label: "Case Studies", ariaLabel: "Project Case Studies", href: "#" }
      ]
    },
    {
      label: "Contact",
      bgColor: "#271E37",
      textColor: "#fff",
      links: [
        { label: "Email", ariaLabel: "Email us", href: "#" },
        { label: "Twitter", ariaLabel: "Twitter", href: "#" },
        { label: "LinkedIn", ariaLabel: "LinkedIn", href: "#" }
      ]
    }
  ];

  return (
    <div className="w-full h-[400px] relative bg-gradient-to-br from-gray-900 to-black rounded-xl overflow-hidden">
      <CardNav
        logo="https://via.placeholder.com/120x40/fff/000?text=LOGO"
        logoAlt="Company Logo"
        items={items}
        baseColor="#fff"
        menuColor="#000"
        buttonBgColor="#111"
        buttonTextColor="#fff"
        ease="power3.out"
      />
    </div>
  );
};

const CircularGalleryDemo = () => {
  return (
    <div className="w-full h-[600px] relative bg-black border border-white/10 rounded-xl overflow-hidden">
      <CircularGallery bend={3} textColor="#ffffff" borderRadius={0.05} scrollEase={0.02} />
    </div>
  );
};

const StackDemo = () => {
  const images = [
    { id: 1, img: "https://images.unsplash.com/photo-1480074568708-e7b720bb3f09?q=80&w=500&auto=format" },
    { id: 2, img: "https://images.unsplash.com/photo-1449844908441-8829872d2607?q=80&w=500&auto=format" },
    { id: 3, img: "https://images.unsplash.com/photo-1452626212852-811d58933cae?q=80&w=500&auto=format" },
    { id: 4, img: "https://images.unsplash.com/photo-1572120360610-d971b9d7767c?q=80&w=500&auto=format" }
  ];

  return (
    <div className="w-full h-[400px] relative bg-black border border-white/10 rounded-xl overflow-hidden flex items-center justify-center">
      <Stack
        randomRotation={true}
        sensitivity={180}
        sendToBackOnClick={false}
        cardDimensions={{ width: 200, height: 200 }}
        cardsData={images}
      />
    </div>
  );
};

const bubbleMenuCode = `import BubbleMenu from './BubbleMenu';

const items = [
  {
    label: 'home',
    href: '#',
    ariaLabel: 'Home',
    rotation: -8,
    hoverStyles: { bgColor: '#3b82f6', textColor: '#ffffff' }
  },
  // ... more items
];

<BubbleMenu
  logo={<span style={{ fontWeight: 700 }}>RB</span>}
  items={items}
  menuAriaLabel="Toggle navigation"
  menuBg="#ffffff"
  menuContentColor="#111111"
  useFixedPosition={false}
  animationEase="back.out(1.5)"
  animationDuration={0.5}
  staggerDelay={0.12}
/>`;

const cardNavCode = `import CardNav from './CardNav';

const items = [
  {
    label: "About",
    bgColor: "#0D0716",
    textColor: "#fff",
    links: [
      { label: "Company", ariaLabel: "About Company", href: "#" },
      { label: "Careers", ariaLabel: "About Careers", href: "#" }
    ]
  },
  // ... more items
];

<CardNav
  logo="logo.svg"
  logoAlt="Company Logo"
  items={items}
  baseColor="#fff"
  menuColor="#000"
  buttonBgColor="#111"
  buttonTextColor="#fff"
  ease="power3.out"
/>`;

const circularGalleryCode = `import CircularGallery from './CircularGallery';

<div style={{ height: '600px', position: 'relative' }}>
  <CircularGallery 
    bend={3} 
    textColor="#ffffff" 
    borderRadius={0.05} 
    scrollEase={0.02}
  />
</div>`;

const stackCode = `import Stack from './Stack';

const images = [
  { id: 1, img: "https://images.unsplash.com/photo-1480074568708-e7b720bb3f09?q=80&w=500&auto=format" },
  { id: 2, img: "https://images.unsplash.com/photo-1449844908441-8829872d2607?q=80&w=500&auto=format" },
  // ... more images
];
  
<Stack
  randomRotation={true}
  sensitivity={180}
  sendToBackOnClick={false}
  cardDimensions={{ width: 200, height: 200 }}
  cardsData={images}
/>`;

const glassSurfaceCode = `import GlassSurface from './GlassSurface';

// Basic usage
<GlassSurface 
  width={300} 
  height={200}
  borderRadius={24}
  className="my-custom-class"
>
  <h2>Glass Surface Content</h2>
</GlassSurface>

// Custom displacement effects
<GlassSurface
  displace={15}
  distortionScale={-150}
  redOffset={5}
  greenOffset={15}
  blueOffset={25}
  brightness={60}
  opacity={0.8}
  mixBlendMode="screen"
>
  <span>Advanced Glass Distortion</span>
</GlassSurface>`;

const glassSurfaceNavbarCode = `import GlassSurfaceNavbar from './GlassSurfaceNavbar';

<GlassSurfaceNavbar>
  <div className="flex items-center justify-between w-full">
    <div className="text-white font-bold text-xl">Logo</div>
    <div className="flex gap-4 text-white/80">
      <a href="#" className="hover:text-white transition-colors">Home</a>
      <a href="#" className="hover:text-white transition-colors">About</a>
      <a href="#" className="hover:text-white transition-colors">Contact</a>
    </div>
  </div>
</GlassSurfaceNavbar>`;

const GlassSurfaceDemo = () => {
  return (
    <div className="w-full h-[400px] relative bg-black border border-white/10 rounded-xl overflow-hidden flex flex-col items-center justify-center gap-8 p-8">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=2564&auto=format&fit=crop')] bg-cover bg-center opacity-50" />

      <GlassSurface
        width={300}
        height={100}
        borderRadius={24}
        className="z-10"
      >
        <div className="text-white text-xl font-bold">Glass Surface</div>
      </GlassSurface>

      <GlassSurface
        width={300}
        height={100}
        borderRadius={24}
        displace={15}
        distortionScale={-150}
        redOffset={5}
        greenOffset={15}
        blueOffset={25}
        brightness={60}
        opacity={0.8}
        mixBlendMode="screen"
        className="z-10"
      >
        <div className="text-white text-xl font-bold">Distorted Glass</div>
      </GlassSurface>
    </div>
  );
};

const GlassSurfaceNavbarDemo = () => {
  return (
    <div className="w-full h-[400px] relative bg-black border border-white/10 rounded-xl overflow-hidden">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1579546929518-9e396f3cc809?q=80&w=2670&auto=format&fit=crop')] bg-cover bg-center" />

      <div className="relative z-10 p-4">
        <GlassSurfaceNavbar>
          <div className="flex items-center justify-between w-full">
            <div className="text-white font-bold text-xl">GlassNav</div>
            <div className="flex gap-6 text-white/90 font-medium">
              <a href="#" className="hover:text-white transition-colors">Products</a>
              <a href="#" className="hover:text-white transition-colors">Solutions</a>
              <a href="#" className="hover:text-white transition-colors">Pricing</a>
            </div>
            <button className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors backdrop-blur-sm border border-white/10">
              Get Started
            </button>
          </div>
        </GlassSurfaceNavbar>
      </div>

      <div className="absolute bottom-10 left-10 text-white max-w-md">
        <h2 className="text-4xl font-bold mb-4">Glass Morphism Navbar</h2>
        <p className="text-lg text-white/80">
          A beautiful, responsive navbar component with advanced glass morphism effects,
          chromatic aberration, and real-time background blurring.
        </p>
      </div>
    </div>
  );
};

export const componentRegistry = {
  'grid-background': {
    component: GridBackgroundDemo,
    code: `${squaresCode}\n\n${gridBackgroundCode}`
  },
  'variable-proximity': {
    title: 'Variable Proximity',
    description: 'Text that reacts to mouse proximity by changing its font weight and width using variable fonts.',
    component: VariableProximityDemo,
    code: variableProximityCode
  },
  'shuffle': {
    title: 'Shuffle Text',
    description: 'GSAP-powered text animation with shuffle effect on scroll or hover. Features customizable direction, scramble characters, and color transitions.',
    component: ShuffleDemo,
    code: shuffleCode
  },
  'text-pressure': {
    title: 'Text Pressure',
    description: 'Variable font text that responds to mouse proximity with dynamic weight, width, and italic adjustments. Supports stroke effects and responsive scaling.',
    component: TextPressureDemo,
    code: textPressureCode
  },
  'text-cursor': {
    title: 'Text Cursor',
    description: 'Text that follows the cursor with a trail effect using Framer Motion. Configurable spacing, direction, and floating animations.',
    component: TextCursorDemo,
    code: textCursorCode
  },
  'scroll-reveal': {
    title: 'Scroll Reveal',
    description: 'GSAP ScrollTrigger-based text reveal animation with word-by-word fade, blur, and rotation effects as you scroll.',
    component: ScrollRevealDemo,
    code: scrollRevealCode
  },
  'loader': {
    title: '3D Spinner Loader',
    description: 'A purely CSS-based 3D spinning loader with SVG background.',
    component: Loader,
    code: loaderCode
  },
  'page-transition': {
    title: 'Page Transition',
    description: 'Smooth page transitions using Framer Motion. Simulates a slide-up effect.',
    component: PageTransitionDemo,
    code: pageTransitionCode
  },
  'folder': {
    title: 'Folder Animation',
    description: 'An interactive 3D folder that opens on click, revealing animated papers that respond to mouse movement.',
    component: FolderDemo,
    code: folderCode
  },
  'liquid-folder': {
    title: 'Liquid Folder',
    description: 'A glassmorphism-style folder with a "pop-out" animation for its contents on hover.',
    component: LiquidFolderDemo,
    code: liquidFolderCode
  },
  'scrambled-text': {
    title: 'Scrambled Text',
    description: 'Mouse proximity text scramble effect using GSAP. Characters scramble and reveal based on cursor distance.',
    component: ScrambledTextDemo,
    code: scrambledTextCode
  },
  'pixel-transition': {
    title: 'Pixel Transition',
    description: 'GSAP-powered pixelated transition effect between two content states. Perfect for image reveals and content switches.',
    component: PixelTransitionDemo,
    code: pixelTransitionCode
  },
  'laser-flow': {
    title: 'Laser Flow',
    description: 'WebGL shader-based laser beam animation with volumetric fog effects and mouse interaction using Three.js.',
    component: LaserFlowDemo,
    code: laserFlowCode
  },
  'magnet-lines': {
    title: 'Magnet Lines',
    description: 'Magnetic line rotation effect that follows mouse movement. Lines rotate to point towards the cursor.',
    component: MagnetLinesDemo,
    code: magnetLinesCode
  },
  'ghost-cursor': {
    title: 'Ghost Cursor',
    description: 'WebGL-based ghostly cursor trail effect with bloom and film grain post-processing using Three.js.',
    component: GhostCursorDemo,
    code: ghostCursorCode
  },
  'cubes': {
    title: 'Cubes',
    description: '3D cube grid with tilt-on-hover and ripple-on-click effects using GSAP. Features auto-animation and customizable appearance.',
    component: CubesDemo,
    code: cubesCode
  },
  'image-trail': {
    title: 'Image Trail',
    description: 'Mouse-following image trail effect with GSAP animations. Images appear and fade along the cursor path.',
    component: ImageTrailDemo,
    code: imageTrailCode
  },
  'scroll-stack': {
    title: 'Scroll Stack',
    description: 'Smooth scroll-based card stacking animation using Lenis. Cards stack and scale as you scroll through content.',
    component: ScrollStackDemo,
    code: scrollStackCode
  },
  'bubble-menu': {
    title: 'Bubble Menu',
    description: 'Animated bubble navigation menu with GSAP. Features expandable menu items with customizable colors and rotation effects.',
    component: BubbleMenuDemo,
    code: bubbleMenuCode
  },
  'card-nav': {
    title: 'Card Nav',
    description: 'Expandable card-based navigation menu with GSAP animations. Features smooth height transitions and categorized links.',
    component: CardNavDemo,
    code: cardNavCode
  },
  'circular-gallery': {
    title: 'Circular Gallery',
    description: 'WebGL-based curved image gallery using OGL. Features smooth scrolling, curved layout, and customizable bend effect.',
    component: CircularGalleryDemo,
    code: circularGalleryCode
  },
  'stack': {
    title: 'Stack',
    description: 'Draggable card stack with 3D rotation effects using Framer Motion. Cards can be dragged and sent to the back.',
    component: StackDemo,
    code: stackCode
  },
  'glass-surface': {
    title: 'Glass Surface',
    description: 'Advanced glass morphism component with SVG filters, chromatic aberration, and real-time background distortion.',
    component: GlassSurfaceDemo,
    code: glassSurfaceCode
  },
  'glass-surface-navbar': {
    title: 'Glass Surface Navbar',
    description: 'A specialized glass morphism navbar component with full-width layout and optimized blur effects.',
    component: GlassSurfaceNavbarDemo,
    code: glassSurfaceNavbarCode
  },
  'dome-gallery': {
    title: 'Dome Gallery',
    description: 'An interactive 3D dome gallery that allows users to drag and rotate to view images.',
    component: () => <div className='w-full h-[600px] relative'><DomeGallery /></div>,
    code: `import DomeGallery from './DomeGallery';

export default function App() {
  return (
    <div style={{ width: '100vw', height: '100vh' }}>
      <DomeGallery />
    </div>
  );
}`
  }
};
